import { type CreateOrder, type Order, OrderSchema } from '../data/order';
import type { OrderOptions } from '../data/order-options';
import { compare } from '../helpers/column-sort';
import { downloadBlob } from '../helpers/download-file';
import {
  ApiResponseError,
  createMultipleHandler,
  createSingleHandler,
  handleVoidResponse,
} from '../helpers/fetch-wrapper';
import { storesBaseUrl as baseUrl, StoreScopedService } from './base';

// The v2 edit endpoints answer 409 for two unrelated reasons, and the body's
// `code` is what tells them apart — mirrors the constants in
// lambdas/api/api.go. Never infer the cause from which keys the body happens
// to carry: a third 409 added later would be misread as one of these two.
const ORDER_VERSION_CONFLICT_CODE = 'ORDER_VERSION_CONFLICT';
const ORDER_NOT_DRAFT_CODE = 'ORDER_NOT_DRAFT';
// Unlike the two above, this one comes from the v1 create endpoint: the vendor
// already has an open draft, possibly one this tab's cache has never seen.
const ORDER_DRAFT_EXISTS_CODE = 'ORDER_DRAFT_EXISTS';

// ORDER_VERSION_CONFLICT carries `order`: the server's current order, for the
// client to rebase its pending edits onto (lambdas/api/api_orders.go
// mapOrderOpError). The OTHER 409, ORDER_NOT_DRAFT, carries no payload and
// stays an ordinary ApiResponseError.
export class OrderVersionConflictError extends ApiResponseError {
  latest: Order;

  constructor(error: ApiResponseError, latest: Order) {
    super(error.message, error.status, error.body);
    this.name = 'OrderVersionConflictError';
    this.latest = latest;
  }
}

const orderHandler = createSingleHandler(OrderSchema);

// Response handler for the three v2 order-edit endpoints (batchSetOrderItems,
// allocateOrderDeal, setOrderOptions). All three return the full order, so the
// shared single handler covers success and every ordinary failure; the one
// thing it can't know is that an ORDER_VERSION_CONFLICT 409 is a rebase
// instruction rather than a failure, so that is all this adds on top.
async function handleOrderResponse(response: Response): Promise<Order> {
  try {
    return await orderHandler(response);
  } catch (error) {
    if (
      !(error instanceof ApiResponseError) ||
      error.status !== 409 ||
      error.body?.code !== ORDER_VERSION_CONFLICT_CODE
    ) {
      throw error;
    }

    let latest: Order;
    try {
      latest = OrderSchema.parse(error.body.order);
    } catch {
      // The embedded `order` failed schema validation (backend/frontend
      // schema drift, or a malformed payload) — fall back to the original
      // ApiResponseError rather than letting a raw ZodError escape.
      // classifyOrderConflict and handleFlushError only know how to recover
      // from ApiResponseError-shaped failures.
      throw error;
    }

    throw new OrderVersionConflictError(error, latest);
  }
}

// The two 409s above are both caller-owned recovery paths rather than failures
// to report, but they recover differently — one rebases onto `latest`, the
// other navigates away. Classifying in one place keeps the generic mutation
// hook's "don't toast this" set and the overlay hook's "here's what to do
// about it" set from drifting apart.
export type OrderConflict = 'version-exhausted' | 'not-draft' | 'draft-exists';

export function classifyOrderConflict(error: unknown): OrderConflict | null {
  // Deliberately keyed on the class, not on ORDER_VERSION_CONFLICT: a conflict
  // whose embedded `order` failed schema validation arrives here as a plain
  // ApiResponseError with no `latest` to rebase onto. Callers suppress the
  // generic toast for every non-null classification, so claiming
  // 'version-exhausted' without a usable order would swallow it silently.
  if (error instanceof OrderVersionConflictError) {
    return 'version-exhausted';
  }

  if (error instanceof ApiResponseError && error.status === 409) {
    if (error.body?.code === ORDER_NOT_DRAFT_CODE) {
      return 'not-draft';
    }

    if (error.body?.code === ORDER_DRAFT_EXISTS_CODE) {
      return 'draft-exists';
    }

    // Anything else — an unrecognized code, or none at all — is a 409 we have
    // no recovery for. null means "report it normally", which beats guessing
    // 'not-draft' and bouncing the user off an order they can still edit.
    return null;
  }

  return null;
}

// Mirrors lambdas/api/services.ItemUpdate. `cases`/`units` are nullable
// pointers server-side: null clears the user override so the row falls back to
// the server suggestion, which is a different state from an explicit 0.
export type ItemUpdate = {
  product_id: string;
  cases: number | null;
  units: number | null;
};

// The name the browser saves the order file under, read off Content-Disposition.
//
// RFC 6266 allows the value either quoted or as a bare token, and allows other
// parameters after it. Our own handler always quotes and sends nothing else
// (lambdas/api/api_orders_download.go:64), so the loose forms are hardening
// rather than a live fix — but both failure modes are silent, and neither is
// visible to the store owner as anything but a file with the wrong name.
export function dispositionFilename(disposition: string | null): string {
  const match = disposition?.match(/filename=(?:"([^"]*)"|([^;\s]+))/);

  return match?.[1] || match?.[2] || 'order-download';
}

export class OrderService extends StoreScopedService {
  getOrdersByStore(storeId: string): Promise<Order[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/orders?status=draft,sending,sent,failed`)
      .then(createMultipleHandler(OrderSchema))
      .then((orders: Order[]) => {
        return orders.sort((o1, o2) => compare(o2.updated_at, o1.updated_at));
      });
  }

  getOrderByStore(storeId: string, orderId: string): Promise<Order> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/orders/${orderId}`)
      .then(createSingleHandler(OrderSchema));
  }

  createOrder(storeId: string, order: CreateOrder): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders`, order)
      .then(createSingleHandler(OrderSchema));
  }

  submitOrder(storeId: string, order: Order, method: string): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders/${order.id}/submit`, {
        order,
        send_method: method,
      })
      .then(createSingleHandler(OrderSchema));
  }

  // v2 item write: one or more overrides set (or cleared, with null) in a
  // single op — one version bump, one broadcast, no inter-item contention.
  // `cases: null` clears the override and returns the row to its suggestion.
  batchSetOrderItems(storeId: string, orderId: string, updates: ItemUpdate[]): Promise<Order> {
    return this.fetchWrapper
      .patch(`${baseUrl}/${storeId}/orders/${orderId}/items`, { updates })
      .then(handleOrderResponse);
  }

  // The unload-path twin of batchSetOrderItems: same endpoint, same body, but
  // dispatched so it outlives the document (see FetchWrapper.keepaliveJson).
  // Nothing is returned because nothing is left to read a response — by the
  // time one arrives the page is gone.
  batchSetOrderItemsKeepalive(storeId: string, orderId: string, updates: ItemUpdate[]): void {
    this.fetchWrapper.keepaliveJson('PATCH', `${baseUrl}/${storeId}/orders/${orderId}/items`, {
      updates,
    });
  }

  // The path segment is the product FAMILY id (volume-pricing family), not a
  // deal entity. The server owns the DOH allocation.
  allocateOrderDeal(
    storeId: string,
    orderId: string,
    familyId: string,
    cases: number
  ): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders/${orderId}/deals/${familyId}/allocate`, { cases })
      .then(handleOrderResponse);
  }

  setOrderOptions(storeId: string, orderId: string, options: OrderOptions): Promise<Order> {
    return this.fetchWrapper
      .patch(`${baseUrl}/${storeId}/orders/${orderId}/options`, options)
      .then(handleOrderResponse);
  }

  // setOrderOptions' unload twin — see batchSetOrderItemsKeepalive.
  setOrderOptionsKeepalive(storeId: string, orderId: string, options: OrderOptions): void {
    this.fetchWrapper.keepaliveJson(
      'PATCH',
      `${baseUrl}/${storeId}/orders/${orderId}/options`,
      options
    );
  }

  async downloadOrder(storeId: string, orderId: string, part?: number): Promise<void> {
    const url =
      part !== undefined
        ? `${baseUrl}/${storeId}/orders/${orderId}/download?part=${part}`
        : `${baseUrl}/${storeId}/orders/${orderId}/download`;
    const response = await this.fetchWrapper.get(url);

    await handleVoidResponse(response, 'Download');

    const filename = dispositionFilename(response.headers.get('Content-Disposition'));

    const blob = await response.blob();
    downloadBlob(blob, filename);
  }

  // `doFetch` throws for 401/403/402 only, so without this check every other
  // failure — a 409, a 500 — resolved `true`. The optimistic removal in
  // useDeleteOrder then stood, no toast appeared, and the owner watched an
  // order vanish that was still on the server until they reloaded.
  //
  // A 404 is the exception, and resolves: the order is already gone, which is
  // the outcome the caller asked for. That covers a double-click and a retry
  // after a dropped response, neither of which should raise an error.
  async deleteOrder(order: Order): Promise<boolean> {
    const response = await this.fetchWrapper.delete(
      `${baseUrl}/${order.store_id}/orders/${order.id}`
    );

    if (response.status !== 404) {
      await handleVoidResponse(response, 'Delete');
    }

    return true;
  }
}
